import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

import { getAdminUserQueryKey } from '../../entities/adminUser/AdminUserEntityProvider';
import { adminUserAtom, emptyAdminUser } from '../../entities/entityAtoms';
import { isLoggingOutRef } from '../helpers';
import type { LogoutType } from '../types/auth';
import { useLogoutMessage } from './useLogoutMessage';

export const useLogout = () => {
    const stytchClient = useStytchB2BClient();
    const { session } = useStytchMemberSession();
    const sessionRef = useRef(session);
    const showLogoutMessage = useLogoutMessage();
    const queryClient = useQueryClient();
    const setAdminUser = useSetAtom(adminUserAtom);

    useEffect(() => {
        sessionRef.current = session;
    }, [session]);

    const logOut = useCallback(
        async (logoutType?: LogoutType) => {
            // Drop the signed-in admin before revoke returns, and ignore an
            // in-flight admin-user response that would write them back.
            isLoggingOutRef.current = true;
            setAdminUser(emptyAdminUser);
            await queryClient.cancelQueries({
                queryKey: [getAdminUserQueryKey],
            });
            queryClient.removeQueries({ queryKey: [getAdminUserQueryKey] });

            try {
                // Revoke Stytch session.
                if (sessionRef.current !== null) {
                    await stytchClient.session.revoke();
                    sessionRef.current = null;
                    if (logoutType != null) {
                        showLogoutMessage(logoutType);
                    }
                }

                // Ensures we don't navigate back to whatever page the member was on after they log back in.
                // This should be a route that does not require authentication.
                router.navigate('/sign-in');
            } finally {
                isLoggingOutRef.current = false;
            }
        },
        [queryClient, setAdminUser, showLogoutMessage, stytchClient],
    );

    return { logOut };
};
